/**
 * O botão que leva a uma tela pergunta o que aquela tela exige — issue #408.
 *
 * O defeito: no contexto de uma empresa, "Aplicar Riscos" aparecia na tela de riscos
 * ocupacionais, e clicar nele dava em recusa. A guarda da rota faz quatro perguntas —
 * permissão, vínculo, perspectiva e módulo contratado — e o controle fazia uma só, a
 * permissão, que é a única das quatro que a lista do manifesto carrega. As outras três moram
 * no menu, no contexto ativo e no contrato, e nenhuma tela olhava para elas.
 *
 * A decisão fica aqui, e não em cada tela, porque ela é a mesma em todas: a medição da issue
 * achou 237 controles que levam a uma rota declarada. Escrever a condição em cada um é
 * reescrevê-la 237 vezes, e é assim que catorze delas divergem no dia em que a regra mudar.
 *
 * Nunca some: bloqueado, o botão continua à vista, desabilitado, com o motivo no tooltip —
 * a política está em `explicarEmVezDeSumir`. Controle que não é botão (ação de linha, item de
 * menu) faz a mesma pergunta pelo `useControle`.
 */

import React from 'react';
import { Button, Tooltip, type ButtonProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import type { Exigencia } from '../../authorization/pode';
import { useVeredito } from '../../authorization/useVeredito';

export interface RouteActionButtonProps extends Omit<ButtonProps, 'href' | 'onClick'> {
  /** A tela a que o controle leva. É por ela que as quatro perguntas são feitas. */
  para: string;
  /**
   * O que essa tela exige — a mesma declaração que está na rota, em `App.tsx`. Quem garante
   * que as duas não divergem é o teste `controle-declara-acesso`, que casa uma com a outra.
   */
  exige: Exigencia;
  /**
   * O que fazer no lugar de navegar. Serve ao controle que abre um diálogo ou dispara uma
   * ação na própria tela: a pergunta de acesso continua sendo a da tela de destino.
   */
  aoAgir?: () => void;
}

export const RouteActionButton: React.FC<RouteActionButtonProps> = ({
  para,
  exige,
  aoAgir,
  children,
  ...props
}) => {
  const navigate = useNavigate();
  const veredito = useVeredito(para, exige);

  if (veredito.liberado) {
    return (
      <Button {...props} onClick={aoAgir ?? (() => navigate(para))}>
        {children}
      </Button>
    );
  }

  // O `<span>` existe porque botão desabilitado não emite evento de mouse, e sem ele o
  // tooltip — que aqui é a única explicação que a pessoa recebe — nunca abriria.
  return (
    <Tooltip title={veredito.explicacao ?? ''}>
      <span>
        <Button {...props} disabled>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
};

export default RouteActionButton;
